
import Page from '@root/common/base/Page';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import PodcastSkeletonLoader from '@root/common/components/base/loader/PodcastSkeletonLoader.vue';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import PodcastCategoryService from '@root/modules/podcast/services/podcastCategory.service';
import PodcastRadio from '@root/modules/podcast/components/PodcastRadio.vue';
import { PodcastAsArticle, PodcastCategory } from '@root/modules/podcast/types/podcast';
import Meta from '@root/common/utils/Meta';
import { stripTags } from '@root/common/filters/htmlFormater';
import getPodcastAdsSettings from '@root/modules/podcast/utils/getPodcastAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';

interface Data {
  episode: PodcastAsArticle | null;
  category: PodcastCategory | null;
  isMobileView: boolean;
  loaded: boolean;
}

interface Computed {
  disabledAdsList: string[];
  showPlayer: boolean;
}

export default Page.extend<Data, unknown, Computed, unknown>({
  components: {
    AdsLayout,
    PodcastSkeletonLoader,
    PodcastRadio,
  },
  data() {
    return {
      category: null,
      episode: null,
      isMobileView: false,
      loaded: false,
    };
  },
  computed: {
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    showPlayer() {
      return Boolean(this.$player?.state.currentlyPlaying.audioId);
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  async fetch() {
    try {
      const { params } = this.$route;
      const id = Number(params.id);
      const podcastEpisodeService = new PodcastEpisodeService();
      const episodes = await podcastEpisodeService.fetch({ id: [id] });
      if (episodes?.length) {
        this.episode = episodes[0] as PodcastAsArticle;

        // Get same category articles for playlist
        const podcastCategoryService = new PodcastCategoryService();
        const categoryWithArticles = await podcastCategoryService.fetch({ id: [96604123], getArticles: true });
        if (categoryWithArticles) {
          this.category = categoryWithArticles;
        }

        this.setPageAdsSettings(getPodcastAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: this.episode }));

        this.loaded = true;
      } else {
        // 404?
      }
    } catch (e) {
      this.handlePageError(e);
    }
  },
  mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
  },
  head() {
    if (this.episode) {
      const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

      meta.title = this.episode.seoFields?.seoTitle || stripTags(this.episode.content.title.html);
      meta.ogTitle = this.episode.seoFields?.seoTitle || stripTags(this.episode.content.title.html);
      meta.description = this.episode.seoFields?.seoDescription || this.episode.content.lead?.text || '';
      meta.ogDescription = this.episode.content.lead?.text || '';

      return {
        title: meta.title,
        meta: meta.data,
        style: meta.style,
        link: meta.link,
        script: meta.script,
        __dangerouslyDisableSanitizers: ['style'],
      };
    }

    return {};
  },
});
